import type { PaigeClient, components } from '@paige/api-client';
import { ApiError, unwrap, type Problem } from './http';

type S = components['schemas'];

/** The Customer page's partner billing on Ademero's invoice (ADR-0036): a company's pause, quotes and card move. */
export interface CompanyLedgerApi {
  suspend(tenantId: string, reason: string): Promise<S['StaffPartnerPanel'] | null>;
  resume(tenantId: string, reason: string): Promise<S['StaffPartnerPanel'] | null>;
  quotes(tenantId: string): Promise<S['EnterpriseQuote'][]>;
  /** The key makes a resent request record one quote. */
  recordQuote(
    tenantId: string,
    input: S['EnterpriseQuoteInput'],
    key: string,
  ): Promise<S['EnterpriseQuote']>;
  withdrawQuote(tenantId: string, quoteId: string): Promise<S['EnterpriseQuote']>;
  /** `preview: true` answers what the move would be and changes nothing. */
  cardMove(tenantId: string, input: S['CardMoveInput']): Promise<S['CardMove']>;
  callOffCardMove(tenantId: string, reason: string): Promise<S['CardMoveCalledOff']>;
}

/** The partner account page: a paying account's channel, standing, companies and statements. */
export interface PartnerAccountsApi {
  find(q: string, cursor: string | null): Promise<S['PartnerAccountPage']>;
  account(zoho: string): Promise<S['StaffPartnerAccount']>;
  channel(zoho: string): Promise<S['PartnerInvoiceChannel']>;
  setChannel(
    zoho: string,
    input: S['PartnerInvoiceChannelInput'],
    key: string,
  ): Promise<S['PartnerInvoiceChannel']>;
  suspend(
    zoho: string,
    input: S['PartnerAccountSuspendInput'],
  ): Promise<S['PartnerAccountSuspension']>;
  resume(zoho: string, reason: string): Promise<S['PartnerAccountSuspension']>;
  companies(zoho: string, cursor: string | null): Promise<S['PartnerAccountCompanyPage']>;
  statements(zoho: string, cursor: string | null): Promise<S['PartnerStatementPage']>;
  statement(zoho: string, statementId: string): Promise<S['StaffPartnerStatement']>;
  /** `key` is the line's: minted when the dialog opens, so a retried or double-clicked ask writes one line. */
  adjust(
    zoho: string,
    input: S['StatementAdjustmentInput'],
    key: string,
  ): Promise<S['PartnerStatementLine']>;
  markPaid(number: string, input: S['StatementPaidInput']): Promise<S['PartnerStatementSummary']>;
  voidAndReissue(
    number: string,
    input: S['StatementVoidInput'],
  ): Promise<S['PartnerStatementSummary']>;
}

/** A switch of channel refused for companies billed the other way: the API names them. */
export class ChannelRefusal extends ApiError {
  readonly companies: { tenantId: string; renewsOn: string | null }[];
  constructor(status: number, problem: Partial<Problem> & { companies?: unknown }) {
    super(status, problem);
    this.companies = Array.isArray(problem.companies)
      ? (problem.companies as ChannelRefusal['companies'])
      : [];
  }
}

/** One statement's QuickBooks import, and a month's of every account (yyyy-MM). */
export const statementCsvUrl = (number: string): string =>
  `/v1/admin/partner-statements/${encodeURIComponent(number)}/csv`;
export const monthStatementsCsvUrl = (month: string): string =>
  `/v1/admin/partner-statements.csv?month=${encodeURIComponent(month)}`;

export function companyLedgerApi(client: PaigeClient): CompanyLedgerApi {
  return { ...pauses(client), ...quoteCalls(client), ...moves(client) };
}

const tenant = (tenantId: string) => ({ path: { tenantId } });

function pauses(client: PaigeClient): Pick<CompanyLedgerApi, 'suspend' | 'resume'> {
  const path = '/admin/tenants/{tenantId}/partner-billing/suspension' as const;
  return {
    async suspend(tenantId, reason) {
      return (
        unwrap(await client.POST(path, { params: tenant(tenantId), body: { reason } })) ?? null
      );
    },
    async resume(tenantId, reason) {
      const params = { ...tenant(tenantId), query: { reason } };
      return unwrap(await client.DELETE(path, { params })) ?? null;
    },
  };
}

function quoteCalls(
  client: PaigeClient,
): Pick<CompanyLedgerApi, 'quotes' | 'recordQuote' | 'withdrawQuote'> {
  return {
    async quotes(tenantId) {
      const params = tenant(tenantId);
      return unwrap(await client.GET('/admin/tenants/{tenantId}/enterprise-quotes', { params }))
        .quotes;
    },
    async recordQuote(tenantId, body, key) {
      const params = { ...tenant(tenantId), header: { 'Idempotency-Key': key } };
      return unwrap(
        await client.PUT('/admin/tenants/{tenantId}/enterprise-quote', { params, body }),
      );
    },
    async withdrawQuote(tenantId, quoteId) {
      const params = { path: { tenantId, quoteId } };
      return unwrap(
        await client.DELETE('/admin/tenants/{tenantId}/enterprise-quote/{quoteId}', { params }),
      );
    },
  };
}

function moves(client: PaigeClient): Pick<CompanyLedgerApi, 'cardMove' | 'callOffCardMove'> {
  const path = '/admin/tenants/{tenantId}/card-move' as const;
  return {
    async cardMove(tenantId, body) {
      return unwrap(await client.POST(path, { params: tenant(tenantId), body }));
    },
    async callOffCardMove(tenantId, reason) {
      return unwrap(
        await client.DELETE(path, { params: { ...tenant(tenantId), query: { reason } } }),
      );
    },
  };
}
